// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// USGS real-time GeoJSON feed client + parser. The parser is DOM-free and
// unit-tested against realistic and malformed feature shapes.
import type { FeedResult, FeedSelection, Quake } from './types';

const BASE = 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary';

/** Build the USGS feed URL for a magnitude band + time period. */
export function feedUrl(sel: FeedSelection): string {
  const band = sel.band === 'all' ? 'all' : sel.band; // 'all' | '2.5' | '4.5' | 'significant'
  return `${BASE}/${band}_${sel.period}.geojson`;
}

function toNum(v: unknown, fallback = NaN): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Parse a USGS FeatureCollection into normalised Quake records. Features with a
 * non-finite magnitude, missing geometry, or missing id are dropped rather than
 * poisoning the model with NaN.
 */
export function parseFeed(json: any): FeedResult {
  const generated = toNum(json?.metadata?.generated, Date.now());
  const title = typeof json?.metadata?.title === 'string' ? json.metadata.title : 'USGS Earthquakes';
  const feats: any[] = Array.isArray(json?.features) ? json.features : [];
  const quakes: Quake[] = [];
  for (const f of feats) {
    const p = f?.properties;
    const g = f?.geometry;
    if (!p || !g || g.type !== 'Point' || !Array.isArray(g.coordinates)) continue;
    const id = typeof f.id === 'string' && f.id ? f.id : String(p.code ?? '');
    if (!id) continue;
    const mag = toNum(p.mag);
    if (!Number.isFinite(mag)) continue;
    const lon = toNum(g.coordinates[0]);
    const lat = toNum(g.coordinates[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    const depth = toNum(g.coordinates[2], 0);
    quakes.push({
      id,
      mag,
      magType: typeof p.magType === 'string' ? p.magType : '',
      time: toNum(p.time, generated),
      updated: toNum(p.updated, toNum(p.time, generated)),
      place: typeof p.place === 'string' && p.place ? p.place : 'Unknown location',
      lat,
      lon,
      depth,
      felt: toNum(p.felt, 0) || 0,
      cdi: toNum(p.cdi, 0) || 0,
      mmi: toNum(p.mmi, 0) || 0,
      alert: typeof p.alert === 'string' ? p.alert : '',
      tsunami: toNum(p.tsunami, 0) || 0,
      sig: toNum(p.sig, 0) || 0,
      type: typeof p.type === 'string' ? p.type : 'earthquake',
      url: typeof p.url === 'string' ? p.url : '',
    });
  }
  // Newest first — the feed is roughly time-ordered but don't rely on it.
  quakes.sort((a, b) => b.time - a.time);
  return { quakes, generated, title };
}

/** Fetch + parse the live feed. Throws on network / non-200 for the caller to surface. */
export async function fetchFeed(sel: FeedSelection, signal?: AbortSignal): Promise<FeedResult> {
  const res = await fetch(feedUrl(sel), { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`USGS feed returned ${res.status}`);
  const json = await res.json();
  return parseFeed(json);
}
